import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonProps, Checkbox, Chip, Link, Tooltip, Typography } from '@mui/material';
import {
	ArrowDownward,
	ArrowDropDown,
	Check,
	ContentCopy,
	HorizontalRule,
	KeyboardArrowDown,
	OpenInNew,
} from '@mui/icons-material';
import { DateTime } from 'luxon';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { useGetCountryByISOCode } from 'utils/getCountry';
import { COLORS } from 'services/constants/colors';
import { EIconButtons } from 'services/constants/iconsTypes';
import { TTableStructureButton } from 'services/constants/types';
import { ICON_BUTTONS } from './constants';
import { EasTooltip } from 'ui/EasTooltip';
import { EasChip } from 'ui/EasChip';
// import { COLORS } from 'services/constants/colors';

interface EasTableValueProps {
	type: ETableStructureType;
	value: any;
	item?: any;
	tableStructureItem?: any;
	selectedRow?: number | null;
	index?: number;
	handleAction?: (event: any, id: number, selector: string) => void;
	size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
	bold?: boolean;
	tooltip?: string;
	chip?: boolean;
}

const MAX_VISIBLE_COUNTRY_CODES = 5;

export const EasTableValue: React.FC<EasTableValueProps> = ({
	type,
	value,
	item,
	tableStructureItem,
	selectedRow,
	index,
	handleAction,
	size = 's',
	bold = false,
	tooltip,
	chip = false,
}) => {
	const registeredCountry = useGetCountryByISOCode(value);
	// console.log('EasTableValue item', item);
	// console.log('registeredCountry', registeredCountry);
	const [isCopied, setIsCopied] = useState<boolean>(false);

	// Helper function to wrap content with tooltip if tooltip prop is provided
	const wrapWithTooltip = (
		content: React.ReactNode,
		fallbackTooltip?: string,
		keyProp?: string,
	): React.ReactNode => {
		if (!content) return content;

		// Helper to strip 'title' prop from the top-level child to avoid MUI warning
		const stripNativeTitle = (element: React.ReactElement): React.ReactElement => {
			const { title, ...restProps } = (element.props ?? {}) as Record<string, unknown>;
			return title !== undefined ? React.cloneElement(element, { ...restProps }) : element;
		};

		// Detect if element is already a MUI Tooltip to avoid nested tooltips
		const isTooltipElement = (element: React.ReactElement): boolean => {
			const elementType: any = element.type;
			return (
				elementType === Tooltip ||
				elementType === EasTooltip ||
				elementType?.muiName === 'Tooltip' ||
				elementType?.displayName === 'Tooltip'
			);
		};

		// If tooltip prop is present, always wrap elements with Tooltip
		if (tooltip && React.isValidElement(content)) {
			if (isTooltipElement(content)) return content;
			return (
				<Tooltip
					key={keyProp}
					title={tooltip ?? ''}
					arrow
					sx={{
						backgroundColor: 'primary.light',
						'&.MuiTooltip-arrow': {
							color: 'primary.light',
							borderColor: 'primary.light!important',
						},
					}}
				>
					{stripNativeTitle(content as React.ReactElement)}
				</Tooltip>
			);
		}

		// If no tooltip prop but fallback provided and content is an element, use fallback
		if (!tooltip && fallbackTooltip && React.isValidElement(content)) {
			if (isTooltipElement(content)) return content;
			return (
				<Tooltip key={keyProp} title={fallbackTooltip} arrow>
					{stripNativeTitle(content as React.ReactElement)}
				</Tooltip>
			);
		}

		// If content is not a ReactElement or no tooltip/fallback, return as is
		return content;
	};

	const fontSize = useMemo(() => {
		return size === 'xs'
			? '0.6rem'
			: size === 's'
				? '0.7rem'
				: size === 'm'
					? '0.8rem'
					: size === 'l'
						? '0.9rem'
						: size === 'xl'
							? '1.0rem'
							: size === 'xxl'
								? '1.4rem'
								: '0.7rem';
	}, [size]);
	const chipHeight = useMemo(() => {
		return size === 'xs'
			? '1rem'
			: size === 's'
				? '1.1rem'
				: size === 'm'
					? '1.4rem'
					: size === 'l'
						? '1.6rem'
						: size === 'xl'
							? '1.8rem'
							: size === 'xxl'
								? '2.0rem'
								: '1.1rem';
	}, [size]);
	const renderCountryValue = () =>
		chip ? (
			<Chip
				label={
					<CountryWithFlag
						countryCode={value as string}
						size={size}
						showLabel={true} //{type === ETableStructureType.COUNTRY ||}
						tooltip={false}
					/>
				}
				sx={{
					backgroundColor: COLORS.BACKGROUND_SECONDARY,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
				}}
			/>
		) : (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'start',
					width: '100%',
				}}
			>
				<CountryWithFlag
					countryCode={value as string}
					size={size}
					showLabel={true} //{type === ETableStructureType.COUNTRY}
					tooltip={false}
				/>
			</Box>
		);

	const renderDateValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						// color: 'primary.main',
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDateTimeValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy, HH:mm')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						// color: 'primary.main',
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDateMonthValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('MM.yyyy')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDatePeriodValue = () => {
		if (!value) return null;

		const dates: string[] = String(value).split(' - ');
		const formatted = dates
			.map((date) => DateTime.fromISO(date).toFormat('dd.MM.yyyy'))
			.join(' - ');

		return (
			<Chip
				label={formatted}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					color: COLORS.font_status,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						color: COLORS.font_status,
					},
				}}
			/>
		);
	};

	const renderAmountValue = () => {
		if (value === null) return null;
		const amount = value === '' ? 0 : Number(value ?? 0);
		const currency = tableStructureItem?.currency?.(item) ?? 'EUR';

		if (value === '—') return <Typography sx={{ fontSize, color: COLORS.font_main }}>—</Typography>;

		const breakdown = tableStructureItem?.breakdown?.(item);
		if (breakdown) {
			return (
				<Box
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'end',
						gap: '2px',
					}}
				>
					<AmountAndCurrency
						size={Number(fontSize.replace('rem', ''))}
						amount={amount}
						currency={currency}
						bold
					/>
					<KeyboardArrowDown sx={{ fontSize: '0.9rem', color: COLORS.font_main }} />
					{breakdown}
				</Box>
			);
		}

		return (
			<AmountAndCurrency
				size={Number(fontSize.replace('rem', ''))}
				amount={amount}
				currency={currency}
				bold
			/>
		);
	};

	// const renderUserValue = () => {
	// 	if (!value) return null;

	// 	return <UserAvatar user={value as TUser} size="xxs" tooltip />;
	// };

	// const renderUsersValue = () => {
	// 	if (!value) return null;

	// 	return (
	// 		<Box sx={{ display: 'flex', flexDirection: 'row', gap: '2px' }}>
	// 			{value.map((user: TUser, idx: number) => (
	// 				<UserAvatar key={String((user as any)?.id ?? idx)} user={user} size="xxs" tooltip />
	// 			))}
	// 		</Box>
	// 	);
	// };

	const renderStatusValue = (statusLabel: string, statusColor: string) => {
		// const statusLabel =
		// 	filter.filter?.find((filterItem: any[]) => filterItem[0] === status)?.[1] ?? status;

		// const statusColor =
		// 	filter.colors?.find((color: any[]) => color[0] === status)?.[1] ?? 'primary.light';

		// Intentionally derive text color if needed in the future
		if (!statusLabel) {
			return null;
		}

		return (
			<EasChip label={statusLabel} bgcolor={statusColor} color={COLORS.font_status} size="small" />
		);
	};

	// Removed unused renderStatusNoBgValue

	const renderLinkValue = () => (
		<Typography
			component="span"
			sx={{
				color: COLORS.font_main,
				fontSize,
				textDecoration: 'none',
				fontWeight: 'bold',
				cursor: 'pointer',
			}}
		>
			{value !== null ? (value as string) : ''}
		</Typography>
	);

	const renderIconValue = () => {
		if (!tableStructureItem?.icons) return null;

		const matchedIcon = tableStructureItem.icons.find((icon: any) => icon.value === value);
		if (!matchedIcon) return null;

		return wrapWithTooltip(
			React.createElement(ICON_BUTTONS[matchedIcon.icon as EIconButtons], {
				color: matchedIcon.color ?? 'primary',
				fontSize: 'small',
				sx: { fontSize: '1.1rem', p: 0, m: 0, width: '1.1rem', verticalAlign: 'middle' },
			}),
			tableStructureItem.name ?? (value as string),
		);
	};

	const renderBooleanValue = () => {
		return value === true ? (
			<Check
				color="primary"
				fontSize="small"
				sx={{ fontSize: '1.1rem', p: 0, m: 0, verticalAlign: 'middle' }}
			/>
		) : // <Chip
		// 	label="Yes"
		// 	sx={{
		// 		backgroundColor: COLORS.PRIMARY,
		// 		color: COLORS.SECONDARY,
		// 		fontSize,
		// 		borderRadius: '12px',
		// 		height: chipHeight,
		// 		fontWeight: 'bold',
		// 		width: '42px',
		// 		p: 0,
		// 	}}
		// />
		value === false ? (
			// вставить символ длинного тире
			// <Typography sx={{ fontSize, color: COLORS.PRIMARY, fontWeight: 'bold' }}>—</Typography>
			<HorizontalRule
				sx={{
					fontSize: '1.1rem',
					p: 0,
					m: 0,
					verticalAlign: 'middle',
					color: COLORS.PRIMARY,
				}}
			/>
		) : // <Chip
		// 	label="No"
		// 	sx={{
		// 		backgroundColor: COLORS.SECONDARY,
		// 		color: COLORS.PRIMARY,
		// 		fontSize,
		// 		borderRadius: '12px',
		// 		height: chipHeight,
		// 		fontWeight: 'bold',
		// 		width: '42px',
		// 		p: 0,
		// 	}}
		// />
		null;
	};

	const renderButtonValue = () => (
		<Button
			color="primary"
			// variant={selectedRow === index ? 'contained' : 'outlined'}
			// size="small"
			variant="contained"
			sx={{
				borderRadius: '12px',
				textTransform: 'none',
				height: '24px',
				padding: '4px 6px!important',
				margin: '0px',
				border: '0.5px solid #016193!important',
				fontSize,
				color: 'white!important',
			}}
			onClick={(event) => handleAction?.(event, item?.id, tableStructureItem?.selector as string)}
		>
			{tableStructureItem?.selector as string}
		</Button>
	);

	const renderButtonsValue = () => {
		const buttons = tableStructureItem?.buttonFilter
			? tableStructureItem.buttonFilter(item)
			: tableStructureItem?.buttons;

		if (!buttons) return null;

		return (
			<Box
				sx={{
					display: 'inline-flex',
					flexDirection: 'row',
					gap: '8px',
				}}
			>
				{buttons.map((button: TTableStructureButton) => {
					const compositeKey = `${button.name}-${button.label ?? ''}-${button.color ?? ''}`;
					const buttonLabel = button.label ?? button.name;
					const color = button.color?.includes('.') ? button.color.split('.')[0] : button.color;

					if (button.icon && button.iconWithName) {
						const IconComponent = ICON_BUTTONS[button.icon];

						return (
							<Tooltip key={compositeKey} title={button.tooltip ?? ''} arrow>
								<Button
									key={compositeKey}
									color={color as ButtonProps['color']}
									variant="contained"
									size="small"
									startIcon={
										IconComponent
											? React.createElement(IconComponent, {
													sx: {
														fontSize: '1rem',
														p: 0,
														m: 0,
														width: '1rem',
														verticalAlign: 'middle',
													},
												})
											: undefined
									}
									sx={{
										borderRadius: '12px',
										textTransform: 'none',
										height: '1.4rem',
										padding: '4px 6px!important',
										margin: '0px',
										fontSize,
										color: 'white!important',
										minWidth: '20px',
										// backgroundColor: button.color ?? 'primary',
										// '&:hover': {
										// 	backgroundColor: button.color ?? 'primary.dark',
										// },
									}}
									onClick={(event) => handleAction?.(event, item?.id, button.name)}
								>
									{buttonLabel}
								</Button>
							</Tooltip>
						);
					}

					if (button.icon) {
						return wrapWithTooltip(
							React.createElement(ICON_BUTTONS[button.icon], {
								onClick: (event) => handleAction?.(event, item?.id, button.name),
								sx: {
									p: 0,
									m: 0,
									verticalAlign: 'middle',
									color: button.color ?? 'primary.main',
									cursor: 'pointer',
								},
							}),
							buttonLabel,
							compositeKey,
						);
					}
					return (
						<Tooltip key={compositeKey} title={button.tooltip ?? ''} arrow>
							<Button
								color={color as ButtonProps['color']}
								variant="contained"
								size="small"
								sx={{
									borderRadius: '12px',
									textTransform: 'none',
									height: '1.4rem',
									padding: '4px 6px!important',
									margin: '0px',
									fontSize,
									color: 'white!important',
									minWidth: '20px',
									// backgroundColor: button.color ?? 'primary',
									// '&:hover': {
									// 	backgroundColor: button.color ?? 'primary.dark',
									// },
								}}
								onClick={(event) => handleAction?.(event, item?.id, button.name)}
							>
								{buttonLabel}
							</Button>
						</Tooltip>
					);
				})}
			</Box>
		);
	};

	const renderTextValue = () => {
		const textContent = (
			<Typography
				sx={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					maxWidth: '100px',
					fontSize,
					color: COLORS.font_main,
				}}
			>
				{value as string}
			</Typography>
		);

		return wrapWithTooltip(textContent, value as string);
	};

	const renderCopyableTextValue = () => {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					gap: 0.5,
					cursor: 'pointer',
				}}
				onClick={() => {
					navigator.clipboard
						.writeText(value as string)
						.then(() => {
							setIsCopied(true);
							setTimeout(() => {
								setIsCopied(false);
							}, 2000);
						})
						.catch(() => {
							// toast.error('Copy failed');
						});
				}}
			>
				<Typography
					sx={{
						fontSize,
						color: COLORS.font_main,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{value as string}
				</Typography>
				<ContentCopy
					sx={{
						fontSize: '0.9rem',
						color: isCopied ? 'success.main' : 'primary.main',
					}}
				/>
			</Box>
		);
	};

	const renderMultilineTextValue = () => (
		<Typography
			sx={{
				fontSize,
				color: COLORS.font_main,
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
				verticalAlign: 'start',
			}}
		>
			{value as string}
		</Typography>
	);

	const renderStringValue = (stringValue: string = value as string) => {
		if (value === null) return <div />;
		const text = (
			<Typography
				component="span"
				sx={{ fontSize, color: COLORS.font_main, fontWeight: bold ? 'bold' : 'normal' }}
			>
				{stringValue}
			</Typography>
		);

		const breakdown = tableStructureItem?.breakdown?.(item);
		if (breakdown) {
			return (
				<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
					{text}
					<KeyboardArrowDown sx={{ fontSize: '0.9rem', color: COLORS.font_main }} />
					{breakdown}
				</Box>
			);
		}

		return text;
	};

	const renderDefaultValue = () => {
		if (value === null) return <div />;
		const stringValue =
			typeof value === 'string' || typeof value === 'number'
				? (String?.(value) ?? '')
				: (String?.(value ?? '') ?? '');
		// console.debug('stringValue', stringValue);

		return (
			<Typography
				component="span"
				sx={{
					fontSize,
					color: COLORS.font_main,
					fontWeight: bold ? 'bold' : 'normal',
				}}
			>
				{stringValue}
			</Typography>
		);
	};

	const renderStatusArrayValue = () => {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: '2px' }}>
				{value &&
					Array.isArray(value) &&
					value.length > 0 &&
					value.map((status: string) => (
						<React.Fragment key={String(status)}>
							{renderStatusValue(
								tableStructureItem?.filter?.find(
									(filterItem: any[]) => filterItem[0] === status,
								)?.[1] ?? status,
								tableStructureItem?.colors?.find((color: any[]) => color[0] === status)?.[1] ??
									COLORS.BACKGROUND_SECONDARY,
							)}
						</React.Fragment>
					))}
			</Box>
		);
	};

	const renderCountryCodeArrayValue = () => {
		if (!value || !Array.isArray(value)) return null;

		const breakdown = tableStructureItem?.breakdown?.(item);
		const visibleCodes: string[] = value.slice(0, MAX_VISIBLE_COUNTRY_CODES);
		const hiddenCount = value.length - visibleCodes.length;

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: breakdown ? 'end' : 'start',
					width: '100%',
					gap: '6px',
				}}
			>
				{visibleCodes.map((countryCode) => (
					<CountryWithFlag
						key={countryCode}
						countryCode={countryCode}
						size="xs"
						showLabel={false}
					/>
				))}
				{hiddenCount > 0 && (
					<Typography
						component="span"
						sx={{ fontSize, color: COLORS.font_main, whiteSpace: 'nowrap' }}
					>
						+{hiddenCount} more
					</Typography>
				)}
				{breakdown && (
					<>
						<KeyboardArrowDown sx={{ fontSize: '0.9rem', color: COLORS.font_main, ml: '-4px' }} />
						{breakdown}
					</>
				)}
			</Box>
		);
	};

	const renderCountryNumberValue = () => {
		return (
			<CountryWithFlag
				countryCode={registeredCountry?.code ?? ''}
				size="l"
				color="primary"
				bold
				tooltip={false}
			/>
		);
	};

	const renderCheckboxValue = () => {
		return (
			<Checkbox
				checked={value}
				size="small"
				color="primary"
				inputProps={{ 'aria-label': 'Checkbox demo' }}
			/>
		);
	};

	const renderOpenInNewTabValue = () => {
		if (tableStructureItem?.link?.(item) === null) return null;
		return (
			<EasTooltip title={tableStructureItem?.tooltip ?? ''} placement="top">
				<Link
					href={tableStructureItem?.link?.(item) ?? ''}
					target="_blank"
					onClick={(event) => event.stopPropagation()}
					sx={{
						color: COLORS.PRIMARY,
						display: 'flex',
						alignItems: 'center',
						gap: '2px',
						textDecoration: 'none',
					}}
				>
					<OpenInNew sx={{ fontSize: fontSize, color: COLORS.PRIMARY }} />
					<Typography
						sx={{
							fontSize,
							color: COLORS.PRIMARY,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							textDecoration: 'none',
							fontWeight: 'bold',
							cursor: 'pointer',
						}}
					>
						{value as string}
					</Typography>
				</Link>
			</EasTooltip>
		);
	};

	// Wrap all render functions with tooltip if tooltip prop is provided
	const renderWithTooltip = (renderFunction: () => React.ReactNode): React.ReactNode => {
		const content = renderFunction();
		if (!content) return null;
		return wrapWithTooltip(content);
	};

	const renderContent = () => {
		switch (type) {
			case ETableStructureType.COUNTRY:
			case ETableStructureType.COUNTRY_CODE:
				return renderWithTooltip(renderCountryValue);

			case ETableStructureType.COUNTRY_CODE_EU:
			case ETableStructureType.COUNTRY_CODE_SUPPORTED:
				return renderWithTooltip(renderCountryValue);

			case ETableStructureType.COUNTRY_CODE_ARRAY:
				return renderWithTooltip(renderCountryCodeArrayValue);

			case ETableStructureType.COUNTRY_NUMBER:
				return renderWithTooltip(renderCountryNumberValue);

			case ETableStructureType.DATE:
				return renderWithTooltip(renderDateValue);

			case ETableStructureType.DATE_TIME:
				return renderWithTooltip(renderDateTimeValue);

			case ETableStructureType.DATE_MONTH:
				return renderWithTooltip(renderDateMonthValue);

			case ETableStructureType.DATE_PERIOD:
				return renderWithTooltip(renderDatePeriodValue);

			case ETableStructureType.AMOUNT:
				return renderWithTooltip(renderAmountValue);

			// case ETableStructureType.USER:
			// 	return renderWithTooltip(renderUserValue);

			// case ETableStructureType.USERS:
			// 	return renderWithTooltip(renderUsersValue);

			case ETableStructureType.STATUS:
				if (
					value === null &&
					!tableStructureItem?.filter?.find((filterItem: any[]) => filterItem[0] === null)
				)
					return null;

				return renderWithTooltip(() =>
					renderStatusValue(
						tableStructureItem?.filter?.find(
							(filterItem: any[]) => String(filterItem[0]) === String(value),
						)?.[1] ?? value,
						tableStructureItem?.colors?.find(
							(color: any[]) => String(color[0]) === String(value),
						)?.[1] ?? COLORS.BACKGROUND_SECONDARY,
					),
				);

			case ETableStructureType.STATUS_NO_BG:
				if (
					value === null &&
					!tableStructureItem?.filter?.find((filterItem: any[]) => filterItem[0] === null)
				)
					return null;
				bold = true;

				return renderWithTooltip(() =>
					renderStringValue(
						tableStructureItem?.filter?.find(
							(filterItem: any[]) => String(filterItem[0]) === String(value),
						)?.[1] ?? value,
					),
				);

			case ETableStructureType.STATUS_ARRAY:
				return renderWithTooltip(renderStatusArrayValue);

			case ETableStructureType.LINK:
				return renderWithTooltip(renderLinkValue);

			// case ETableStructureType.COMMENTS:
			// 	return renderCommentsValue();

			case ETableStructureType.ICON:
				return renderIconValue();

			case ETableStructureType.BOOLEAN:
				return renderWithTooltip(renderBooleanValue);

			// case ETableStructureType.BUTTON:
			// 	return renderWithTooltip(renderButtonValue);

			case ETableStructureType.BUTTONS:
				return renderWithTooltip(renderButtonsValue);

			case ETableStructureType.TEXT:
				return renderTextValue();

			case ETableStructureType.MULTILINE_TEXT:
				return renderWithTooltip(renderMultilineTextValue);

			case ETableStructureType.COPYABLE_TEXT:
				return renderWithTooltip(renderCopyableTextValue);

			// case ETableStructureType.ARRAY_ID:
			// 	return renderWithTooltip(renderArrayIdValue);
			case ETableStructureType.STRING:
				return renderWithTooltip(renderStringValue);

			case ETableStructureType.CHECKBOX:
				return renderWithTooltip(renderCheckboxValue);

			case ETableStructureType.OPEN_IN_NEW_TAB:
				return renderWithTooltip(renderOpenInNewTabValue);

			default:
				return renderWithTooltip(renderDefaultValue);
		}
	};

	const content = renderContent();

	// Always return a JSX element or null
	if (!content) {
		return null;
	}

	// If content is already a JSX element, return it
	if (React.isValidElement(content)) {
		return content;
	}

	// If content is a primitive value, wrap it in a span
	return <span>{content}</span>;
};
